import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PlantCard from '../components/PlantCard';
import plantsData from '../data/plants.json';

export default function Home() {
  const [topRatedPlants, setTopRatedPlants] = useState([]);

  useEffect(() => {
    const topRated = plantsData.filter(plant => plant.rating > 4.7);
    setTopRatedPlants(topRated);
  }, []);

  const tips = [
    {
      icon: 'fas fa-tint',
      title: 'Watering',
      description: 'Most indoor plants prefer their soil to dry out slightly between waterings. Overwatering is the most common cause of plant problems.'
    },
    {
      icon: 'fas fa-sun',
      title: 'Light Requirements',
      description: 'Place plants according to their light needs. Some thrive in bright indirect light, while others prefer low-light conditions.'
    },
    {
      icon: 'fas fa-seedling',
      title: 'Fertilizing',
      description: 'Feed your plants during their growing season (spring and summer) with a balanced, water-soluble fertilizer every 2-4 weeks.'
    }
  ];

  const experts = [
    {
      name: 'Sarah Johnson',
      specialty: 'Botanist & Plant Care Specialist',
      image: 'https://lf.dealmakersforums.com/wp-content/uploads/2023/07/Johnson_Official_1-1383x1536.jpg'
    },
    {
      name: 'Michael Chen',
      specialty: 'Indoor Plant Designer',
      image: 'https://harvardtechnologyreview.com/wp-content/uploads/2023/10/image.jpeg'
    },
    {
      name: 'Emily Rodriguez',
      specialty: 'Plant Health Consultant',
      image: 'https://assets.civicengine.com/uploads/candidate/headshot/399595/399595.jpg'
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-50 to-green-50 py-20">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between lg:px-20 gap-20">
            <div className="md:w-1/2 mb-8 md:mb-0">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Bring Nature <span className="text-primary-600">Indoors</span>
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                Create a peaceful and healthy living space with our collection of beautiful indoor plants. Perfect for any season and any home, these lush greens not only enhance your décor but also purify the air, boost your mood, and bring a touch of nature indoors.

              </p>
              <div className="flex space-x-4">
                <Link to="/plants" className="btn-primary">
                  Explore Plants
                </Link>
                <button className="btn-outline">
                  Book Consultation
                </button>
              </div>
            </div>
            <div className="md:w-1/2">
              <img
                src="https://images.unsplash.com/photo-1501004318641-b39e6451bec6?ixlib=rb-1.2.1&w=800&h=600&fit=crop"
                alt="Indoor Plants"
                className="rounded-lg shadow-2xl w-full lg:w-7/8 mx-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Top Rated Plants */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">
            Top Rated Indoor Plants
          </h2>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            Discover our most loved plants that bring life and freshness to any indoor space.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 lg:px-20 gap-8">
            {topRatedPlants.map(plant => (
              <PlantCard key={plant.plantId} plant={plant} />
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/plants" className="btn-primary">
              View All Plants
            </Link>
          </div>
        </div>
      </section>

      {/* Plant Care Tips */}
      <section className="py-16 bg-primary-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">
            Plant Care Tips
          </h2>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            Essential tips to keep your indoor plants healthy and thriving.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {tips.map((tip, index) => (
              <div key={index} className="card text-center p-6">
                <div className="text-primary-600 text-4xl mb-4">
                  <i className={tip.icon}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {tip.title}
                </h3>
                <p className="text-gray-600">
                  {tip.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Green Experts */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">
            Meet Our Green Experts
          </h2>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            Our team of plant specialists is here to help you with all your plant care needs.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {experts.map((expert, index) => (
              <div key={index} className="card text-center">
                <img
                  src={expert.image}
                  alt={expert.name}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {expert.name}
                  </h3>
                  <p className="text-primary-600 font-medium">
                    {expert.specialty}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Eco Decor Ideas */}
      <section className="py-16 bg-primary-50">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="md:w-1/2">
                <img
                  src="https://thedecorkart.com/cdn/shop/articles/Eco-Friendly_Decor_Ideas_for_a_Sustainable_Home.jpg?v=1716142458&width=1000"
                  alt="Plant Decor"
                  className="rounded-lg w-full"
                />
              </div>
              <div className="md:w-1/2">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Eco Decor Ideas
                </h3>
                <p className="text-gray-600 mb-4">
                  Transform your living space with our creative plant styling ideas. 
                  From minimalist arrangements to lush green corners, discover how to 
                  incorporate plants into your home decor for a fresh, natural look 
                  that improves air quality and boosts your mood.
                </p>
                <button className="btn-primary">
                  Get Inspired
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Plant of the Week */}
      <section className="py-16 bg-primary-600 text-white lg:px-20">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="md:w-1/2 mb-8 md:mb-0">
              <h2 className="text-3xl font-bold mb-4">Plant of the Week</h2>
              <h3 className="text-2xl font-semibold mb-4">Monstera Deliciosa</h3>
              <p className="text-primary-100 mb-6">
                Also known as the Swiss Cheese Plant, this tropical beauty is famous for its unique split leaves. 
                It's an excellent choice for beginners and adds a dramatic touch to any room.
              </p>
              <ul className="space-y-2 text-primary-100 mb-6">
                <li className="flex items-center">
                  <i className="fas fa-check mr-2 text-primary-300"></i>
                  Thrives in bright, indirect light
                </li>
                <li className="flex items-center">
                  <i className="fas fa-check mr-2 text-primary-300"></i>
                  Water when top inch of soil is dry
                </li>
                <li className="flex items-center">
                  <i className="fas fa-check mr-2 text-primary-300"></i>
                  Perfect for humid environments
                </li>
              </ul>
              <button className="btn-outline border-white text-white hover:bg-white hover:text-primary-600">
                Learn More
              </button>
            </div>
            <div className="md:w-1/2">
              <img
                src="https://nurserynisarga.in/wp-content/uploads/2025/01/decore-img.webp"
                alt="Monstera Deliciosa"
                className="rounded-lg shadow-2xl w-full lg:w-3/4 mx-auto"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}